/**
 * Checkout.
 *
 * ── WHAT THE REBUILD WAS FOR ──────────────────────────────────────────────
 * The gateway was rebuilt to Thomas's review and this page had to catch up to
 * it. The old checkout was one 1,362 line screen that put a path chooser, an
 * institution form, a routing gate, a billing block, nine displayed clauses,
 * two consent boxes and a submit button on top of each other and asked the
 * buyer to work out where they were. It wore the gateway's chrome and none of
 * its design.
 *
 * Five things are different and each one is a decision, not a preference.
 *
 * 1. THREE STEPS, ALL THREE VISIBLE. A finite flow finishes. The rail shows
 *    the whole ladder from the first screen, a completed step is a button
 *    back to itself, and no state is lost by going back, because there is one
 *    state object and the steps only choose which part of it is on screen.
 *
 * 2. ONE GRADIENT, ON THE MONEY. The gateway's hero recipe appears exactly
 *    once here, on the order panel. It is sticky beside the form on a wide
 *    screen and ABOVE it on a narrow one, so the answer to "what am I paying"
 *    is never a scroll away. Monthly is the display figure at every step and
 *    on either cadence; the annual total is third; and the last row always
 *    states the real amount that will be charged, because a presentation
 *    hierarchy is not a hiding place.
 *
 * 3. THE QUOTE PATH IS A PEER. A university buyer often cannot use a card at
 *    all. Both paths are radio options of the same size with the same weight
 *    of description, and the quote path has its own confirmation screen
 *    carrying the real quote number the Worker returns.
 *
 * 4. THE DOCUMENTS ARE ON THE PAGE. Both of them, in a scrollable pane, with
 *    one unticked box naming both by version and a button whose own label
 *    says what clicking does. See DocumentPanel.tsx for the reasoning.
 *
 * 5. EVERY STATE IS DRAWN. Validating, submitting, declined, unreachable,
 *    rate limited, network, quote sent, already subscribed, card payments
 *    closed, and the one a closed deployment shows before any of those:
 *    payments are not connected, which renders as a calm designed panel that
 *    moves the buyer to the quote path in one click.
 *
 * ── WHAT OPENS THE CARD PATH (Sep 14 2026) ────────────────────────────────
 * The Worker, not the build. Until the Worker half of card checkout shipped
 * this page was gated on a build-time Stripe publishable key, which hosted
 * Checkout never needed and which cannot know whether a deployment holds
 * the secret key, the webhook secret, the test rehearsal switch or published
 * documents. The page now asks `GET /api/enterprise/checkout/availability`
 * once on mount, and until it answers, or if it never does, the card path is
 * closed: the not-connected panel and the quote path, never a dead button or
 * a fake success. When the answer says Stripe is in test mode, a banner says
 * so. The reason a deployment is closed is never sent to a browser.
 *
 * The card submit posts the flat order `cardOrderPayload` builds to
 * `POST /api/enterprise/checkout/session` (201 with `redirectUrl`) and follows
 * it to Stripe. Stripe returns a buyer who paid to /checkout/complete
 * (CheckoutComplete.tsx), and one who cancelled to this page with
 * `notice=cancelled`. A 503 that says card payments are closed after all
 * closes the card path on this page too, so the buyer is not sent round the
 * same refusal twice. The quote path posts to `POST /api/enterprise/quote`,
 * the flat body that route actually reads.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";

/* The checkout layer. Imported here rather than from main.tsx so it lands
   after gateway.css in the bundle: the order panel restates the hero gradient
   and its print rules have to win over gateway.css's own. */
import "~/app/styles/checkout.css";

import { DocumentPanel } from "~/app/components/checkout/DocumentPanel";
import { OrderSummary } from "~/app/components/checkout/OrderSummary";
import { StepRail } from "~/app/components/checkout/StepRail";
import {
  AlreadySubscribedError,
  BUYER_NOTES_LIMIT,
  CARD_CLOSED,
  CARD_NEXT,
  FAILURE_COPY,
  LEAD_NOTE,
  NOTICE_COPY,
  PATH_OPTIONS,
  ProviderError,
  QUOTE_COMMITS_NOTHING,
  QUOTE_NEXT,
  RENEWAL_REMINDERS,
  SUBMIT_LABEL,
  SUBMIT_LABEL_BUSY,
  TEST_MODE_NOTICE,
  acceptanceStatement,
  buildAcceptance,
  cardOrderPayload,
  classifyFailure,
  commitmentSentence,
  emptyState,
  noticeBody,
  failureLines,
  firstErrorKey,
  leadPayload,
  nextStep,
  previousStep,
  quotePayload,
  readAvailability,
  readCheckoutDraft,
  readCheckoutQuery,
  saveCheckoutDraft,
  renewalSentence,
  serverMessageOf,
  stepIndex,
  stepsFor,
  termDates,
  validateStep,
  websiteDomainMismatch,
  type Billing,
  type CardAvailability,
  type CardOrderPayload,
  type CheckoutPath,
  type CheckoutState,
  type Errors,
  type FailureKind,
  type LeadPayload,
  type Phase,
  type QuoteAccepted,
  type QuotePayload,
  type ReturnNotice,
  type StepId,
} from "~/app/components/checkout/checkout-model";
import { request } from "~/app/lib/api";
import { trackCheckout, type CheckoutContext } from "~/app/lib/analytics";
import { GatewayShell, LOGIN_PATH, SelectedMark } from "~/app/components/GatewayShell";
import {
  Button,
  Checkbox,
  Field,
  Input,
  Select,
  buttonClass,
} from "~/app/components/primitives";
import { cn } from "~/app/lib/cn";
import { formatDate } from "~/app/lib/format";
import {
  DEFAULT_BAND_ID,
  INVOICE_NET_DAYS,
  LEGAL,
  QUOTE_RESPONSE_BUSINESS_DAYS,
  QUOTE_VALID_DAYS,
  ROUTING_QUESTIONS,
  bandById,
  type BandId,
} from "~/app/lib/pricing";

export type { CheckoutPath } from "~/app/components/checkout/checkout-model";

/* ==========================================================================
   Props
   ========================================================================== */

export interface CheckoutProps {
  path?: CheckoutPath;
  bandId?: BandId;
  students?: number | null;
  notice?: ReturnNotice;
  /**
   * Whether card checkout is open, in the shape the Worker's availability
   * route answers. Unset, the page asks the Worker once on mount and treats
   * the card path as closed until it hears back. Set, for a test or a story,
   * nothing is asked.
   */
  cardAvailability?: CardAvailability;
  /** Resolves to a Stripe hosted Checkout URL. Rejects to show a failure state. */
  onSubmitCard?: (order: CardOrderPayload) => Promise<{ redirectUrl: string }>;
  /** Resolves with the quote number the Worker minted. */
  onSubmitQuote?: (payload: QuotePayload) => Promise<QuoteAccepted>;
  /**
   * Keeps step 1 when the buyer continues. Resolves to the lead id the Worker
   * answered, or null. Never awaited by the page, and never allowed to fail it.
   */
  onKeepLead?: (payload: LeadPayload) => Promise<string | null>;
  chrome?: boolean;
  className?: string;
}

/* ==========================================================================
   The page
   ========================================================================== */

export function Checkout({
  path: pathProp,
  bandId: bandProp,
  students: studentsProp,
  notice: noticeProp,
  cardAvailability,
  onSubmitCard,
  onSubmitQuote,
  onKeepLead,
  chrome = true,
  className,
}: CheckoutProps) {
  const query = useMemo(
    () => readCheckoutQuery(typeof window === "undefined" ? "" : window.location.search),
    [],
  );

  const bandId = bandProp ?? query.bandId ?? DEFAULT_BAND_ID;
  const band = bandById(bandId);
  const quotedBand = band.monthly === null || band.annual === null;
  const estimatedStudents = studentsProp ?? query.students;
  const notice = noticeProp ?? query.notice;

  /* The Worker's answer, asked once. Closed until it arrives, and closed if
     it never does. `closedAtSubmit` is the session route saying the same
     thing later (PAYMENTS_UNCONFIGURED, PRICE_UNAVAILABLE): from then on this
     page shows the card path as closed rather than offering it again. */
  const [askedAvailability, setAskedAvailability] = useState<CardAvailability>(CARD_CLOSED);
  const [closedAtSubmit, setClosedAtSubmit] = useState(false);
  useEffect(() => {
    if (cardAvailability !== undefined) return;
    let cancelled = false;
    void fetchCardAvailability().then((answer) => {
      if (!cancelled) setAskedAvailability(answer);
    });
    return () => {
      cancelled = true;
    };
  }, [cardAvailability]);
  const availability = closedAtSubmit ? CARD_CLOSED : (cardAvailability ?? askedAvailability);
  const paymentsConnected = availability.card;

  /* ---- one state object, three views of it ----
     A buyer coming back from Stripe gets back what they typed (section 14 of
     checkout-model), read once, and only when the address carries a notice.
     The address's path and billing still win over the draft's. */
  const [restoredDraft] = useState(() => (notice ? readCheckoutDraft(sessionStore()) : null));
  const [state, setState] = useState<CheckoutState>(() => {
    const base = emptyState(
      quotedBand ? "quote" : (pathProp ?? query.path ?? "card"),
      query.billing ?? "monthly",
    );
    if (!restoredDraft) return base;
    return { ...restoredDraft.state, path: base.path, billing: query.billing ?? restoredDraft.state.billing, accepted: false };
  });
  const path: CheckoutPath = quotedBand ? "quote" : state.path;
  const steps = stepsFor(path);

  const [step, setStep] = useState<StepId>("institution");
  const [furthest, setFurthest] = useState<StepId>("institution");
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [blocker, setBlocker] = useState<string | null>(null);
  const [phase, setPhase] = useState<Phase>("idle");
  const [failure, setFailure] = useState<FailureKind | null>(null);
  const [serverSaid, setServerSaid] = useState<string | null>(null);
  const [accepted, setAcceptedQuote] = useState<QuoteAccepted | null>(null);
  const [readToEnd, setReadToEnd] = useState({ terms: false, addendum: false });

  /* How far a visitor gets (lib/analytics.ts): the stage, the path, the band
     and the billing interval, never a field's value. A ref, so a keystroke
     re-sends nothing. */
  const analyticsContext = useRef<CheckoutContext>({ path, band: bandId, billing: state.billing });
  analyticsContext.current = { path, band: bandId, billing: state.billing };
  useEffect(() => {
    trackCheckout(step, analyticsContext.current);
  }, [step]);
  useEffect(() => {
    if (notice) trackCheckout("returned", analyticsContext.current, { notice });
  }, [notice]);

  const formRef = useRef<HTMLFormElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const movedOnce = useRef(false);

  const options = { paymentsConnected, quotedBand };

  /* The page's own ground. `eg-co-page` is what checkout.css section 0 hooks
     to raise the campus wash on this page and only this page: /pricing opens
     on a dark masthead and its wash is masked for that, and checkout has no
     masthead at all. Every screen this component can render carries it,
     including the two terminal ones, or the ground would change under a buyer
     who has done nothing but submit. */
  const pageClass = cn("eg-co-page", className);
  const errors: Errors = validateStep(step, { ...state, path }, options);

  const dates = useMemo(() => termDates(), []);
  const renewalDisclosure = renewalSentence(band, state.billing, dates);
  const statement = acceptanceStatement(state.institution.legalName);

  const showError = (key: string): string | undefined =>
    touched[key] ? errors[key] : undefined;

  const patch = useCallback((next: Partial<CheckoutState>) => {
    setState((current) => ({ ...current, ...next }));
  }, []);

  /* ---- moving between steps ---------------------------------------------
     Focus follows the step, or a keyboard reader is left at the top of a page
     whose middle silently changed. It does NOT run on first paint, which
     would steal focus from the skip link. ---------------------------------- */
  useEffect(() => {
    if (!movedOnce.current) {
      movedOnce.current = true;
      return;
    }
    headingRef.current?.focus();
  }, [step]);

  function goTo(target: StepId): void {
    setBlocker(null);
    setStep(target);
    if (stepIndex(target) > stepIndex(furthest)) setFurthest(target);
  }

  function advance(): void {
    const found = validateStep(step, { ...state, path }, options);
    const keys = Object.keys(found);
    if (keys.length > 0) {
      setTouched((current) => {
        const next = { ...current };
        for (const key of keys) next[key] = true;
        return next;
      });
      const first = firstErrorKey(found);
      setBlocker(first ? (found[first] ?? "Check the highlighted fields.") : null);
      if (first) focusField(formRef.current, first);
      return;
    }
    if (step === "institution") keepLead();
    goTo(nextStep(step));
  }

  /* Step 1, kept on Continue (Sep 15 2026): P3 follows up by email on a
     checkout that is never finished, and the line under the button says so.
     Never awaited, so the buyer moves on whatever the Worker answers. The id
     it answers makes the next Continue update the same lead. */
  const leadId = useRef<string | null>(restoredDraft?.leadId ?? null);
  function keepLead(): void {
    const payload = leadPayload(state, bandId, path, estimatedStudents ?? null, leadId.current);
    void (onKeepLead ?? defaultKeepLead)(payload).then(
      (answered) => {
        if (answered) leadId.current = answered;
      },
      () => undefined,
    );
  }

  /* ---- submitting ---- */
  async function submit(): Promise<void> {
    setBlocker(null);
    setFailure(null);
    setServerSaid(null);

    const found = validateStep("review", { ...state, path }, options);
    const keys = Object.keys(found);
    if (keys.length > 0) {
      setTouched((current) => {
        const next = { ...current };
        for (const key of keys) next[key] = true;
        return next;
      });
      const first = firstErrorKey(found);
      setBlocker(first ? (found[first] ?? null) : null);
      return;
    }

    trackCheckout("submitted", analyticsContext.current);
    setPhase("submitting");
    try {
      if (path === "card") {
        const order = cardOrderPayload(
          { ...state, path },
          bandId,
          estimatedStudents,
          buildAcceptance({
            institution: state.institution,
            statement,
            renewalDisclosure,
            readToEnd,
          }),
        );
        const send = onSubmitCard ?? defaultSubmitCard;
        const { redirectUrl } = await send(order);
        trackCheckout("card_redirect", analyticsContext.current);
        saveCheckoutDraft(sessionStore(), { ...state, path }, leadId.current);
        window.location.assign(redirectUrl);
        return;
      }

      const send = onSubmitQuote ?? defaultSubmitQuote;
      const result = await send(
        quotePayload({ ...state, path }, band, estimatedStudents),
      );
      setAcceptedQuote(result);
      setPhase("sent");
      trackCheckout("quote_sent", analyticsContext.current);
    } catch (error) {
      if (error instanceof AlreadySubscribedError) {
        setServerSaid(error.serverMessage);
        setPhase("already");
        trackCheckout("already_subscribed", analyticsContext.current);
        return;
      }
      const kind = classifyFailure(error);
      if (kind === "payments-closed") setClosedAtSubmit(true);
      setPhase("failed");
      setFailure(kind);
      trackCheckout("failed", analyticsContext.current, { failure: kind });
      setServerSaid(serverMessageOf(error));
    }
  }

  /* ---- terminal screens ---- */
  if (phase === "sent") {
    return (
      <QuoteSent
        chrome={chrome}
        className={pageClass}
        state={state}
        bandName={band.name}
        quote={accepted}
        validUntil={formatDate(dates.quoteValidUntil, { style: "long" })}
      />
    );
  }

  if (phase === "already") {
    return (
      <AlreadySubscribed
        chrome={chrome}
        className={pageClass}
        legalName={state.institution.legalName}
        serverSaid={serverSaid}
      />
    );
  }

  const busy = phase === "submitting";
  const currentStep = steps[stepIndex(step)];

  return (
    <GatewayShell
      current="checkout"
      chrome={chrome}
      mainId="checkout-main"
      skipLabel="Skip to the form"
      className={pageClass}
    >
      <section className="eg-co-top">
        <p className="e-label">
          {path === "card" ? "Register" : "Request a quote"}
        </p>
        <h1 className="e-h1">
          {path === "card"
            ? "Three steps, then payment."
            : "Three steps, then a quote in writing."}
        </h1>
        <p className="eg-co-lede">
          {path === "card"
            ? "Nothing is charged until the last click, and the last click says so."
            : QUOTE_COMMITS_NOTHING}
        </p>
      </section>

      {notice ? (
        <div className="eg-co-notice">
          <Banner tone={notice === "declined" ? "warn" : "neutral"} title={NOTICE_COPY[notice].title}>
            {noticeBody(notice, restoredDraft !== null)}
          </Banner>
        </div>
      ) : null}

      {path === "card" && availability.card && availability.mode === "test" ? (
        <div className="eg-co-notice">
          <TestModeBanner />
        </div>
      ) : null}

      <div className="eg-co">
        <OrderSummary
          className="eg-co-order"
          band={band}
          billing={state.billing}
          onBillingChange={(billing: Billing) => patch({ billing })}
          path={path}
          estimatedStudents={estimatedStudents}
        />

        <div className="eg-co-main">
          <StepRail steps={steps} current={step} furthest={furthest} onGo={goTo} />

          <form
            ref={formRef}
            noValidate
            onSubmit={(event) => {
              event.preventDefault();
              if (step === "review") void submit();
              else advance();
            }}
          >
            <section className="eg-co-panel" aria-labelledby="step-heading">
              <header className="eg-co-panel-head">
                <p className="eg-co-panel-count">
                  Step {stepIndex(step) + 1} of {steps.length}
                </p>
                <h2 id="step-heading" className="e-h2" tabIndex={-1} ref={headingRef}>
                  {currentStep?.label ?? "Your institution"}
                </h2>
              </header>

              {step === "institution" ? (
                <InstitutionStep
                  state={state}
                  patch={patch}
                  showError={showError}
                  markTouched={(key) => setTouched((t) => ({ ...t, [key]: true }))}
                />
              ) : null}

              {step === "payment" ? (
                <PaymentStep
                  state={state}
                  path={path}
                  quotedBand={quotedBand}
                  paymentsConnected={paymentsConnected}
                  patch={patch}
                  showError={showError}
                  markTouched={(key) => setTouched((t) => ({ ...t, [key]: true }))}
                  onChoosePath={(next) => {
                    setBlocker(null);
                    patch({ path: next });
                    // Switching path changes which fields step two requires,
                    // so a review step reached under the other path is no
                    // longer earned. Without this, a buyer who reached review
                    // on the card path and then switched could submit a quote
                    // with no billing address on it.
                    setFurthest((current) =>
                      stepIndex(current) > stepIndex("payment") ? "payment" : current,
                    );
                  }}
                />
              ) : null}

              {step === "review" ? (
                <ReviewStep
                  state={state}
                  path={path}
                  renewalDisclosure={renewalDisclosure}
                  statement={statement}
                  readToEnd={readToEnd}
                  onReadToEnd={(tab) =>
                    setReadToEnd((current) =>
                      // The same object once it is already read, so a reader
                      // scrolling at the bottom does not re-render the page.
                      tab === "terms"
                        ? current.terms ? current : { ...current, terms: true }
                        : current.addendum ? current : { ...current, addendum: true },
                    )
                  }
                  onEdit={goTo}
                  onAcceptChange={(value) => {
                    patch({ accepted: value });
                    setBlocker(null);
                  }}
                  acceptError={showError("accepted")}
                />
              ) : null}

              {blocker ? (
                <p className="eg-co-blocker" role="alert">
                  {blocker}
                </p>
              ) : null}

              {failure ? (
                <FailureBanner
                  kind={failure}
                  serverSaid={serverSaid}
                  path={path}
                  onRetry={() => {
                    setFailure(null);
                    setPhase("idle");
                    void submit();
                  }}
                  onSwitchToQuote={() => {
                    patch({ path: "quote" });
                    setFailure(null);
                    setPhase("idle");
                    // The clamp onChoosePath applies, for the same reason:
                    // review was reached on the card path, and the quote
                    // path's step two asks for a billing address the card
                    // path never did. The payments-closed failure makes this
                    // button the one a buyer is sent to.
                    setFurthest((current) =>
                      stepIndex(current) > stepIndex("payment") ? "payment" : current,
                    );
                    goTo("payment");
                  }}
                />
              ) : null}

              <div className="eg-co-actions">
                {step === "institution" ? (
                  <a className={buttonClass("ghost")} href="/pricing">
                    Back to pricing
                  </a>
                ) : (
                  <Button variant="ghost" onClick={() => goTo(previousStep(step))}>
                    Back
                  </Button>
                )}

                {step === "review" ? (
                  <Button
                    type="submit"
                    size="lg"
                    aria-busy={busy}
                    aria-disabled={busy}
                    className="eg-co-go"
                  >
                    {submitLabel(path, busy)}
                  </Button>
                ) : (
                  <Button type="submit" size="lg" className="eg-co-go">
                    Continue
                  </Button>
                )}
              </div>

              {step === "institution" ? <p className="eg-co-after">{LEAD_NOTE}</p> : null}

              {step === "review" && path === "card" ? (
                <p className="eg-co-after">
                  The next screen is Stripe. P3 never sees your card number.
                </p>
              ) : null}
            </section>
          </form>

          <section className="eg-co-next" aria-labelledby="next-heading">
            <h2 id="next-heading" className="e-label">
              What happens next
            </h2>
            <ol className="eg-co-next-list">
              {(path === "card" ? CARD_NEXT : QUOTE_NEXT).map((line) => (
                <li key={line.slice(0, 40)}>{line}</li>
              ))}
            </ol>
          </section>
        </div>
      </div>
    </GatewayShell>
  );
}

/* The label reads a constant, and so does the sentence beside it. See
   SUBMIT_LABEL in checkout-model.ts for why the wording has not changed and
   why it can only change in one place. */
function submitLabel(path: CheckoutPath, busy: boolean): string {
  return busy ? SUBMIT_LABEL_BUSY[path] : SUBMIT_LABEL[path];
}

/* ==========================================================================
   Step 1: who is subscribing
   ========================================================================== */

interface StepProps {
  state: CheckoutState;
  patch: (next: Partial<CheckoutState>) => void;
  showError: (key: string) => string | undefined;
  markTouched: (key: string) => void;
}

function InstitutionStep({ state, patch, showError, markTouched }: StepProps) {
  const { institution } = state;
  const set = (next: Partial<CheckoutState["institution"]>) =>
    patch({ institution: { ...institution, ...next } });

  const domainWarning = websiteDomainMismatch(
    institution.website,
    institution.contactEmail,
  );

  return (
    <>
      <div className="eg-fields">
        <Field label="Institution legal name" error={showError("legalName")} className="eg-span-2">
          <Input
            name="legalName"
            autoComplete="organization"
            value={institution.legalName}
            onChange={(e) => set({ legalName: e.target.value })}
            onBlur={() => markTouched("legalName")}
          />
        </Field>

        <Field label="Institution website" error={showError("website")}>
          <Input
            name="website"
            inputMode="url"
            value={institution.website}
            onChange={(e) => set({ website: e.target.value })}
            onBlur={() => markTouched("website")}
          />
        </Field>

        <Field label="Department or school" hint="Optional. It appears on the invoice as a reference.">
          <Input
            name="department"
            value={institution.department}
            onChange={(e) => set({ department: e.target.value })}
          />
        </Field>

        <Field label="Your full name" error={showError("contactName")}>
          <Input
            name="contactName"
            autoComplete="name"
            value={institution.contactName}
            onChange={(e) => set({ contactName: e.target.value })}
            onBlur={() => markTouched("contactName")}
          />
        </Field>

        <Field
          label="Your title"
          error={showError("contactTitle")}
        >
          <Input
            name="contactTitle"
            autoComplete="organization-title"
            value={institution.contactTitle}
            onChange={(e) => set({ contactTitle: e.target.value })}
            onBlur={() => markTouched("contactTitle")}
          />
        </Field>

        <Field
          label="Your work email"
          error={showError("contactEmail")}
          hint={domainWarning ?? undefined}
          className="eg-span-2"
        >
          <Input
            name="contactEmail"
            type="email"
            autoComplete="email"
            value={institution.contactEmail}
            onChange={(e) => set({ contactEmail: e.target.value })}
            onBlur={() => markTouched("contactEmail")}
          />
        </Field>

        <Field label="Phone" hint="Optional. Only used if a quote needs a question answered.">
          <Input
            name="contactPhone"
            type="tel"
            autoComplete="tel"
            value={institution.contactPhone}
            onChange={(e) => set({ contactPhone: e.target.value })}
          />
        </Field>
      </div>

      <div className="eg-co-gate">
        <h3 className="e-h3">One quick question</h3>

        <Field label={ROUTING_QUESTIONS.publicInstitution}>
          <Select
            name="publicInstitution"
            value={boolToSelect(state.routing.publicInstitution)}
            onChange={(e) =>
              patch({
                routing: { ...state.routing, publicInstitution: selectToBool(e.target.value) },
              })
            }
          >
            <option value="">Choose one</option>
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </Select>
        </Field>
        {state.routing.publicInstitution === true ? (
          <Banner tone="warn" title="You may need a signed agreement instead">
            Some states bar a public institution from agreeing to a limitation
            of liability, a warranty disclaimer or a choice of{" "}
            {LEGAL.governingLaw} law in a click-through. If yours does, do not
            accept online. Write to {LEGAL.contactEmail} and we will route you
            to a signed agreement at the same price. Clause 19 of the Terms says
            the same thing in the contract.
          </Banner>
        ) : null}

      </div>
    </>
  );
}

/* ==========================================================================
   Step 2: how you pay
   ========================================================================== */

function PaymentStep({
  state,
  path,
  quotedBand,
  paymentsConnected,
  patch,
  showError,
  markTouched,
  onChoosePath,
}: StepProps & {
  path: CheckoutPath;
  quotedBand: boolean;
  paymentsConnected: boolean;
  onChoosePath: (path: CheckoutPath) => void;
}) {
  const { address } = state;
  const set = (next: Partial<CheckoutState["address"]>) =>
    patch({ address: { ...address, ...next } });

  return (
    <>
      {quotedBand ? (
        <Banner tone="neutral" title="This band is quoted directly">
          More than 1,000 students, a system, or a multi-campus agreement is
          priced in a conversation, so we can fit the term to your calendar. The
          card path is not available for it.
        </Banner>
      ) : (
        <>
          <div className="eg-co-paths" role="radiogroup" aria-label="How you would like to buy">
            {PATH_OPTIONS.map((option) => (
              <button
                key={option.id}
                type="button"
                role="radio"
                aria-checked={path === option.id}
                className={cn("eg-co-path", path === option.id && "is-on")}
                onClick={() => onChoosePath(option.id)}
              >
                <span className="eg-rung-mark" aria-hidden="true">
                  {path === option.id ? <SelectedMark /> : null}
                </span>
                <span className="eg-co-path-text">
                  <span className="eg-co-path-title">{option.title}</span>
                  <span className="eg-co-path-body">{option.body}</span>
                  <span className="eg-co-path-note">{option.note}</span>
                </span>
              </button>
            ))}
          </div>
          {showError("path") ? (
            <p className="e-field-error" role="alert">
              {showError("path")}
            </p>
          ) : null}
        </>
      )}

      {path === "card" && !paymentsConnected && !quotedBand ? (
        <PaymentsNotConnected onSwitch={() => onChoosePath("quote")} />
      ) : null}

      {path === "card" && paymentsConnected ? (
        <p className="eg-co-hint">
          You accept the Terms on the next step, then Stripe collects the
          payment. P3 never sees your card number.
        </p>
      ) : null}

      {path === "quote" ? (
        <>
          <h3 className="e-h3">Where the quote and the invoice should go</h3>
          <p className="eg-co-hint">
            The billing address goes in the quote header and sets the tax
            treatment.
          </p>
          <div className="eg-fields">
            <Field label="Billing address" error={showError("line1")} className="eg-span-2">
              <Input
                name="line1"
                autoComplete="address-line1"
                value={address.line1}
                onChange={(e) => set({ line1: e.target.value })}
                onBlur={() => markTouched("line1")}
              />
            </Field>
            <Field label="Address line 2" hint="Optional." className="eg-span-2">
              <Input
                name="line2"
                autoComplete="address-line2"
                value={address.line2}
                onChange={(e) => set({ line2: e.target.value })}
              />
            </Field>
            <Field label="City" error={showError("city")}>
              <Input
                name="city"
                autoComplete="address-level2"
                value={address.city}
                onChange={(e) => set({ city: e.target.value })}
                onBlur={() => markTouched("city")}
              />
            </Field>
            <Field label="State" error={showError("state")}>
              <Input
                name="state"
                autoComplete="address-level1"
                value={address.state}
                onChange={(e) => set({ state: e.target.value })}
                onBlur={() => markTouched("state")}
              />
            </Field>
            <Field label="ZIP code" error={showError("postalCode")}>
              <Input
                name="postalCode"
                autoComplete="postal-code"
                value={address.postalCode}
                onChange={(e) => set({ postalCode: e.target.value })}
                onBlur={() => markTouched("postalCode")}
              />
            </Field>
            <Field
              label="Accounts payable email"
              error={showError("accountsPayableEmail")}
              hint="Optional. Given one, the quote and the invoice both copy it."
            >
              <Input
                name="accountsPayableEmail"
                type="email"
                value={address.accountsPayableEmail}
                onChange={(e) => set({ accountsPayableEmail: e.target.value })}
                onBlur={() => markTouched("accountsPayableEmail")}
              />
            </Field>
            <Field
              label="Purchase order number"
              hint="Optional. Given one, we issue the quote and the invoice together."
            >
              <Input
                name="purchaseOrderNumber"
                value={state.purchaseOrderNumber}
                onChange={(e) => patch({ purchaseOrderNumber: e.target.value })}
              />
            </Field>
            <Field
              label="Your fiscal year end"
              hint="Optional. Tell us and the term starts where your budget starts."
            >
              <Input
                name="fiscalYearEnd"
                placeholder="June 30"
                value={state.fiscalYearEnd}
                onChange={(e) => patch({ fiscalYearEnd: e.target.value })}
              />
            </Field>
            <Field
              label="Anything we should know"
              hint={`Optional. Up to ${BUYER_NOTES_LIMIT} characters.`}
              className="eg-span-2"
            >
              <textarea
                name="notes"
                className="e-input"
                rows={3}
                maxLength={BUYER_NOTES_LIMIT}
                value={state.notes}
                onChange={(e) => patch({ notes: e.target.value })}
              />
            </Field>
          </div>
        </>
      ) : null}
    </>
  );
}

/**
 * The state that is true today. It is a designed panel rather than a disabled
 * button, because a buyer who cannot pay by card tonight can still start a
 * requisition tonight, and the quote path is one click away inside it.
 */
function PaymentsNotConnected({ onSwitch }: { onSwitch: () => void }) {
  return (
    <div className="eg-co-off">
      <span className="eg-co-off-mark" aria-hidden="true">
        <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
          <rect x="2.4" y="5" width="15.2" height="10" rx="2" />
          <path d="M2.4 8.6h15.2" />
        </svg>
      </span>
      <div>
        <h3 className="e-h3">Card payments are not connected yet</h3>
        <p>
          Nothing is broken and nothing was charged. Payments are the last thing
          we switch on, so that everything else can be checked first. You can
          still buy today: take a quote and we will invoice you, which is the
          path a purchase order needs anyway.
        </p>
        <div className="eg-co-off-actions">
          <Button variant="primary" onClick={onSwitch}>
            Switch to a quote
          </Button>
          <a className={buttonClass("secondary")} href={`mailto:${LEGAL.contactEmail}`}>
            Write to {LEGAL.contactEmail}
          </a>
        </div>
      </div>
    </div>
  );
}

/* ==========================================================================
   Step 3: review, and the acceptance
   ========================================================================== */

function ReviewStep({
  state,
  path,
  renewalDisclosure,
  statement,
  readToEnd,
  onReadToEnd,
  onEdit,
  onAcceptChange,
  acceptError,
}: {
  state: CheckoutState;
  path: CheckoutPath;
  renewalDisclosure: string;
  statement: string;
  readToEnd: { terms: boolean; addendum: boolean };
  onReadToEnd: (tab: "terms" | "addendum") => void;
  onEdit: (step: StepId) => void;
  onAcceptChange: (value: boolean) => void;
  acceptError?: string;
}) {
  const { institution, address } = state;

  return (
    <>
      <div className="eg-co-recap">
        <div className="eg-co-recap-head">
          <h3 className="e-h3">Your institution</h3>
          <button type="button" className="eg-co-edit" onClick={() => onEdit("institution")}>
            Edit
            <span className="e-sr-only"> your institution</span>
          </button>
        </div>
        <dl className="eg-co-recap-rows">
          <RecapRow label="Legal name" value={institution.legalName} />
          <RecapRow label="Website" value={institution.website} />
          <RecapRow
            label="Contact"
            value={[institution.contactName, institution.contactTitle]
              .filter(Boolean)
              .join(", ")}
          />
          <RecapRow label="Email" value={institution.contactEmail} />
          {institution.department ? (
            <RecapRow label="Department" value={institution.department} />
          ) : null}
        </dl>
      </div>

      {path === "quote" ? (
        <div className="eg-co-recap">
          <div className="eg-co-recap-head">
            <h3 className="e-h3">Where it goes</h3>
            <button type="button" className="eg-co-edit" onClick={() => onEdit("payment")}>
              Edit
              <span className="e-sr-only"> the billing details</span>
            </button>
          </div>
          <dl className="eg-co-recap-rows">
            <RecapRow
              label="Bill to"
              value={[address.line1, address.line2, `${address.city}, ${address.state}`, address.postalCode]
                .filter((part) => part.trim() !== "" && part.trim() !== ",")
                .join(" / ")}
            />
            {address.accountsPayableEmail ? (
              <RecapRow label="Accounts payable" value={address.accountsPayableEmail} />
            ) : null}
            {state.purchaseOrderNumber ? (
              <RecapRow label="Purchase order" value={state.purchaseOrderNumber} />
            ) : null}
            {state.fiscalYearEnd ? (
              <RecapRow label="Fiscal year end" value={state.fiscalYearEnd} />
            ) : null}
          </dl>
        </div>
      ) : null}

      {path === "card" ? (
        <>
          <div className="eg-co-renewal">
            <h3 className="e-h3">How renewal works</h3>
            <p>{renewalDisclosure}</p>
            {/* The 90 and 45 day notices are about an ANNUAL renewal date; a
                monthly plan has none (Sep 15 2026). */}
            {state.billing === "annual" ? <p className="eg-co-hint">{RENEWAL_REMINDERS}</p> : null}
          </div>

          <div className="eg-co-accept">
            <h3 className="e-h3">What you are agreeing to</h3>
            <DocumentPanel readToEnd={readToEnd} onReadToEnd={onReadToEnd} />

            <Checkbox
              name="accepted"
              checked={state.accepted}
              onChange={(event) => onAcceptChange(event.target.checked)}
              label={statement}
            />

            {acceptError ? (
              <p className="e-field-error" role="alert">
                {acceptError}
              </p>
            ) : null}

            {/* NOT a hand-typed copy of the button's words. It names the
                button by reading the button's own constant, so the two cannot
                drift apart when Thomas rules on the label. */}
            <p className="eg-co-consequence">{commitmentSentence("card")}</p>
          </div>
        </>
      ) : (
        <div className="eg-co-accept">
          <h3 className="e-h3">The documents your quote will reference</h3>
          <p className="eg-co-hint">
            Read them now if it helps. You accept them later, when you send us
            the purchase order number, and we record who accepted and when.
          </p>
          <DocumentPanel readToEnd={readToEnd} onReadToEnd={onReadToEnd} />
          <p className="eg-co-consequence">{QUOTE_COMMITS_NOTHING}</p>
        </div>
      )}
    </>
  );
}

function RecapRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="eg-co-recap-row">
      <dt>{label}</dt>
      <dd>{value.trim() === "" ? "Not given" : value}</dd>
    </div>
  );
}

/* ==========================================================================
   Terminal screens
   ========================================================================== */

function QuoteSent({
  chrome,
  className,
  state,
  bandName,
  quote,
  validUntil,
}: {
  chrome: boolean;
  className?: string;
  state: CheckoutState;
  bandName: string;
  quote: QuoteAccepted | null;
  validUntil: string;
}) {
  const expires =
    quote?.expiresAt != null
      ? formatDate(new Date(quote.expiresAt), { style: "long" })
      : validUntil;

  return (
    <GatewayShell
      current="checkout"
      chrome={chrome}
      mainId="checkout-main"
      skipLabel="Skip to the confirmation"
      className={className}
    >
      <section className="eg-co-done">
        <span className="eg-co-done-mark" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 12.5 10 17.5 19 7" />
          </svg>
        </span>
        <p className="e-label">Request received</p>
        <h1 className="e-h1">Your quote is being written.</h1>
        <p className="eg-co-lede">
          We will email a PDF to{" "}
          {state.institution.contactEmail || "the address you gave us"} within{" "}
          {QUOTE_RESPONSE_BUSINESS_DAYS} business day.
        </p>

        {quote ? (
          <p className="eg-co-quoteno">
            <span className="e-label">Your quote number</span>
            <strong>{quote.quoteNumber}</strong>
          </p>
        ) : null}
      </section>

      <div className="eg-co-done-grid">
        <div className="eg-co-recap">
          <h2 className="e-h3">What the quote will say</h2>
          <dl className="eg-co-recap-rows">
            <RecapRow label="Institution" value={state.institution.legalName} />
            <RecapRow label="Capacity commitment" value={bandName} />
            <RecapRow label="Term" value="12 months" />
            <RecapRow label="Payment terms" value={`Net ${INVOICE_NET_DAYS}`} />
            <RecapRow label="Holds until" value={expires} />
          </dl>
          <p className="eg-co-hint">
            A quote holds for {QUOTE_VALID_DAYS} days, and longer if you ask.
          </p>
        </div>

        <section className="eg-co-next" aria-labelledby="done-next">
          <h2 id="done-next" className="e-label">
            What happens next
          </h2>
          <ol className="eg-co-next-list">
            {QUOTE_NEXT.map((line) => (
              <li key={line.slice(0, 40)}>{line}</li>
            ))}
          </ol>
          <p className="eg-co-hint">
            A question, or a change to the quote: {LEGAL.contactEmail}.
          </p>
        </section>
      </div>
    </GatewayShell>
  );
}

function AlreadySubscribed({
  chrome,
  className,
  legalName,
  serverSaid,
}: {
  chrome: boolean;
  className?: string;
  legalName: string;
  serverSaid: string | null;
}) {
  return (
    <GatewayShell
      current="checkout"
      chrome={chrome}
      mainId="checkout-main"
      skipLabel="Skip to the message"
      className={className}
    >
      <section className="eg-co-done">
        <p className="e-label">Nothing to buy</p>
        <h1 className="e-h1">
          {legalName.trim() === "" ? "That institution" : legalName} already has
          a subscription.
        </h1>
        <p className="eg-co-lede">
          {serverSaid ??
            "Nothing was charged. Sign in to the dashboard, or ask an administrator at your institution to add you."}
        </p>
        <div className="eg-co-actions">
          {/* This host's own sign-in, since Sep 9 2026. Before it existed
              this button sent the buyer to the student platform, the only
              sign-in there was. The nav's Login reads the same constant. */}
          <a className={buttonClass("primary", "lg")} href={LOGIN_PATH}>
            Sign in
          </a>
          <a className={buttonClass("secondary")} href={`mailto:${LEGAL.contactEmail}`}>
            Write to {LEGAL.contactEmail}
          </a>
        </div>
      </section>
    </GatewayShell>
  );
}

/* ==========================================================================
   Banners
   ========================================================================== */

function Banner({
  tone,
  title,
  children,
}: {
  tone: "neutral" | "warn" | "crit" | "good";
  title: string;
  children: ReactNode;
}) {
  return (
    <div className={cn("eg-banner", `tone-${tone}`)} role="note">
      <p className="eg-banner-title">{title}</p>
      <p>{children}</p>
    </div>
  );
}

/** One banner for Stripe's test mode, here and on the page Stripe returns a buyer to. */
export function TestModeBanner() {
  return (
    <Banner tone="neutral" title={TEST_MODE_NOTICE.title}>
      {TEST_MODE_NOTICE.body}
    </Banner>
  );
}

function FailureBanner({
  kind,
  serverSaid,
  path,
  onRetry,
  onSwitchToQuote,
}: {
  kind: FailureKind;
  serverSaid: string | null;
  path: CheckoutPath;
  onRetry: () => void;
  onSwitchToQuote: () => void;
}) {
  const copy = FAILURE_COPY[kind];
  const lines = failureLines(kind, serverSaid);
  return (
    <div className="eg-banner tone-crit" role="alert">
      <p className="eg-banner-title">{copy.title}</p>
      <p>{lines.lead}</p>
      {lines.hint ? <p className="eg-co-hint">{lines.hint}</p> : null}
      <div className="eg-banner-actions">
        {copy.offerRetry ? (
          <Button variant="secondary" onClick={onRetry}>
            Try again
          </Button>
        ) : null}
        {copy.offerQuote && path === "card" ? (
          <Button variant="secondary" onClick={onSwitchToQuote}>
            Take a quote instead
          </Button>
        ) : null}
        {/* Secondary, not ghost: inside a tinted banner a ghost button has no
            edge and reads as bold body text rather than as the way out. On the
            rate-limited state it is the ONLY action, so it has to look like
            one. */}
        <a className={buttonClass("secondary")} href={`mailto:${LEGAL.contactEmail}`}>
          Write to {LEGAL.contactEmail}
        </a>
      </div>
    </div>
  );
}

/* ==========================================================================
   The network layer

   One timeout, one error shape, one place that decides what a status means.
   The Worker's error body is `{ error: { code, message } }` and its own header
   comment says the message is safe to show, so it is shown.
   ========================================================================== */

const REQUEST_TIMEOUT_MS = 20_000;

async function postJson<T>(url: string, body: unknown): Promise<T> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  let response: Response;
  try {
    response = await fetch(url, {
      method: "POST",
      headers: { "content-type": "application/json", accept: "application/json" },
      body: JSON.stringify(body),
      signal: controller.signal,
    });
  } finally {
    clearTimeout(timer);
  }

  const text = await response.text();
  const parsed = safeParse(text);

  if (response.status === 409) {
    throw new AlreadySubscribedError(messageOf(parsed));
  }
  if (!response.ok) {
    throw new ProviderError(response.status, messageOf(parsed), codeOf(parsed));
  }
  return (parsed ?? {}) as T;
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return null;
  }
}

function messageOf(parsed: unknown): string | null {
  if (parsed && typeof parsed === "object" && "error" in parsed) {
    const error = (parsed as { error?: unknown }).error;
    if (error && typeof error === "object" && "message" in error) {
      const message = (error as { message?: unknown }).message;
      if (typeof message === "string" && message.trim() !== "") return message;
    }
  }
  return null;
}

function codeOf(parsed: unknown): string | null {
  if (parsed && typeof parsed === "object" && "error" in parsed) {
    const error = (parsed as { error?: unknown }).error;
    if (error && typeof error === "object" && "code" in error) {
      const code = (error as { code?: unknown }).code;
      if (typeof code === "string") return code;
    }
  }
  return null;
}

/**
 * The route answers 201 with Stripe's hosted Checkout address. It never
 * answers 409, so postJson's already-subscribed reading cannot fire here. The
 * address is followed with `location.assign`, so anything but an https URL is
 * treated as no address at all.
 */
/** This tab's session storage, or null where the browser refuses it (a
 *  private window, blocked site data, server rendering). */
function sessionStore(): Storage | null {
  try {
    return typeof window === "undefined" ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

async function defaultSubmitCard(order: CardOrderPayload): Promise<{ redirectUrl: string }> {
  const body = await postJson<{ redirectUrl?: unknown }>(
    "/api/enterprise/checkout/session",
    order,
  );
  const redirectUrl = body.redirectUrl;
  if (typeof redirectUrl !== "string" || !redirectUrl.startsWith("https://")) {
    throw new ProviderError(502, null, "NO_REDIRECT");
  }
  return { redirectUrl };
}

/**
 * Step 1 to the lead route. Never throws: a lead that does not save costs P3 a
 * follow-up, never the buyer the order, so any failure is simply no id.
 */
async function defaultKeepLead(payload: LeadPayload): Promise<string | null> {
  const raw = await request("/api/enterprise/checkout/lead", { method: "POST", body: payload });
  if (raw === null || (raw.status !== 200 && raw.status !== 201)) return null;
  const answered = raw.json && typeof raw.json === "object" ? (raw.json as { leadId?: unknown }).leadId : null;
  return typeof answered === "string" ? answered : null;
}

/** Whether card checkout is open. Never throws: any failure is the closed answer. */
async function fetchCardAvailability(): Promise<CardAvailability> {
  const raw = await request("/api/enterprise/checkout/availability");
  return raw !== null && raw.status === 200 ? readAvailability(raw.json) : CARD_CLOSED;
}

async function defaultSubmitQuote(payload: QuotePayload): Promise<QuoteAccepted> {
  const body = await postJson<{ quoteNumber?: string; expiresAt?: number }>(
    "/api/enterprise/quote",
    payload,
  );
  return {
    quoteNumber: body.quoteNumber ?? "",
    expiresAt: typeof body.expiresAt === "number" ? body.expiresAt : null,
  };
}

/* ==========================================================================
   Small helpers
   ========================================================================== */

function focusField(form: HTMLFormElement | null, name: string): void {
  form?.querySelector<HTMLElement>(`[name="${name}"]`)?.focus();
}

function boolToSelect(value: boolean | null): string {
  return value === null ? "" : value ? "yes" : "no";
}

function selectToBool(value: string): boolean | null {
  return value === "yes" ? true : value === "no" ? false : null;
}
